import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { getCriancaAtiva, getTodasOfertas, listarAlimentos } from "../../db/queries";
import { ESPECIALIDADE_LABEL, type EspecialidadeProf } from "../../types";
import { FoodPhoto } from "../../ui/FoodPhoto";
import { EscadaMini } from "../../ui/EscadaMini";
import { calcularDerivadosEquipe, painelResponsabilidade } from "../domain";
import { getNotasDaCrianca, listarProfissionais } from "../queries";
import { EspecialidadeBadge } from "../ui/EspecialidadeBadge";
import { TimelineEquipe } from "../ui/TimelineEquipe";

/**
 * P0.3 — timeline interdisciplinar, em dois modos (decisão do Marco):
 *  • por alimento: a SUBIDA de cada alimento na escada, com o handoff entre disciplinas;
 *  • por data: todas as sessões em ordem cronológica (com filtro por disciplina).
 */
export function TimelineIntegrada() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const alimentoFoco = params.get("alimento");

  const crianca = useLiveQuery(getCriancaAtiva);
  const profissionais = useLiveQuery(() => listarProfissionais(true)) ?? [];
  const ofertas = useLiveQuery(getTodasOfertas) ?? [];
  const alimentos = useLiveQuery(() => listarAlimentos(true)) ?? [];
  const notas = useLiveQuery(
    () => (crianca ? getNotasDaCrianca(crianca.id) : Promise.resolve([])),
    [crianca?.id],
  ) ?? [];

  const [modo, setModo] = useState<"alimento" | "data">("alimento");
  const [filtroDisc, setFiltroDisc] = useState<EspecialidadeProf | null>(null);

  const nomePorProfissional = (id: string) =>
    profissionais.find((p) => p.id === id)?.nome ?? "profissional";
  const nomeAlimento = (id: string) => alimentos.find((a) => a.id === id)?.nome ?? "alimento";

  const dEquipe = crianca
    ? calcularDerivadosEquipe(crianca.id, notas)
    : { disciplinas: [] as EspecialidadeProf[] };
  const painel = crianca ? painelResponsabilidade(crianca.id, notas, ofertas, alimentos) : [];

  // notas sem alimento (gerais — psico/nutri sem alvo na escada)
  const gerais = notas.filter((n) => !n.alimento_id);

  const cabecalho = (
    <>
      <button className="btn btn-fantasma" onClick={() => nav(-1)}>← voltar</button>
      <h1 className="tela-titulo">linha do tempo</h1>
      <p className="tela-sub">{crianca ? `${crianca.apelido} · ` : ""}uma criança, todas as disciplinas.</p>
      <div className="seg" role="group" aria-label="modo da timeline">
        <button type="button" className={`seg-opt${modo === "alimento" ? " ativo" : ""}`} aria-pressed={modo === "alimento"} onClick={() => setModo("alimento")}>
          🍎 por alimento
        </button>
        <button type="button" className={`seg-opt${modo === "data" ? " ativo" : ""}`} aria-pressed={modo === "data"} onClick={() => setModo("data")}>
          📅 por data
        </button>
      </div>
    </>
  );

  if (notas.length === 0) {
    return (
      <main className="tela">
        {cabecalho}
        <div className="vazio">
          <span className="emoji" aria-hidden>🔗</span>
          a linha do tempo aparece quando você registrar a 1ª sessão.
        </div>
      </main>
    );
  }

  // ── modo POR DATA ──────────────────────────────────────────────────────
  if (modo === "data") {
    const lista = filtroDisc ? notas.filter((n) => n.especialidade === filtroDisc) : notas;
    return (
      <main className="tela">
        {cabecalho}
        {dEquipe.disciplinas.length > 1 && (
          <div className="chips" style={{ margin: "var(--s4) 0" }}>
            <button className="chip" style={!filtroDisc ? chipSel : undefined} onClick={() => setFiltroDisc(null)}>todas</button>
            {dEquipe.disciplinas.map((e) => (
              <button key={e} className="chip" style={filtroDisc === e ? chipSel : undefined} onClick={() => setFiltroDisc(e)}>
                {ESPECIALIDADE_LABEL[e]}
              </button>
            ))}
          </div>
        )}
        <TimelineEquipe notas={lista} nomePorProfissional={nomePorProfissional} onAbrir={(id) => nav(`/elo/nota/${id}`)} />
      </main>
    );
  }

  // ── modo POR ALIMENTO ──────────────────────────────────────────────────
  const itens = alimentoFoco ? painel.filter((it) => it.alimento_id === alimentoFoco) : painel;
  return (
    <main className="tela">
      {cabecalho}

      {alimentoFoco && (
        <button className="btn btn-fantasma" style={{ marginTop: "var(--s2)" }} onClick={() => setParams({}, { replace: true })}>
          ← ver todos os alimentos
        </button>
      )}

      {itens.length === 0 && (
        <div className="vazio" style={{ marginTop: "var(--s4)" }}>
          nenhuma sessão ligada a um alimento ainda. ligue a sessão a um alimento ao registrar.
        </div>
      )}

      {itens.map((it) => {
        const a = alimentos.find((x) => x.id === it.alimento_id);
        const doAlimento = notas
          .filter((n) => n.alimento_id === it.alimento_id)
          .sort((x, y) => y.data_sessao.localeCompare(x.data_sessao));
        return (
          <section className="secao" key={it.alimento_id} style={{ marginTop: "var(--s5)" }}>
            <div className="card" style={{ marginBottom: "var(--s3)" }}>
              <div className="row">
                <FoodPhoto fotoId={a?.fotos[0]} nome={a?.nome} categoria={a?.categoria} />
                <div className="crescer">
                  <div className="row-entre">
                    <div className="nome">{nomeAlimento(it.alimento_id)}</div>
                    {it.responsavel && (
                      <span className="row" style={{ gap: 4 }}>
                        <span className="meta">vez de</span>
                        <EspecialidadeBadge especialidade={it.responsavel} curto />
                      </span>
                    )}
                  </div>
                  <div className="evol">
                    <EscadaMini passo={it.degrau_atual} />
                    <span className="evol-meta">{it.degrau_atual !== null ? `degrau ${it.degrau_atual}` : "sem degrau ainda"}</span>
                  </div>
                </div>
              </div>
            </div>
            <TimelineEquipe notas={doAlimento} nomePorProfissional={nomePorProfissional} onAbrir={(id) => nav(`/elo/nota/${id}`)} />
          </section>
        );
      })}

      {!alimentoFoco && gerais.length > 0 && (
        <section className="secao" style={{ marginTop: "var(--s5)" }}>
          <h2 className="secao-titulo">sessões gerais (sem alimento)</h2>
          <TimelineEquipe notas={gerais} nomePorProfissional={nomePorProfissional} onAbrir={(id) => nav(`/elo/nota/${id}`)} />
        </section>
      )}
    </main>
  );
}

const chipSel: React.CSSProperties = {
  background: "var(--accent-suave-bg)",
  color: "var(--accent-forte)",
  borderColor: "var(--accent-suave-borda)",
};
